import { useState, useEffect } from 'react';
import { useAdminVendors, useAdminVendor } from './useAdminVendors';
import { isVendorEligibleForSpotlight } from '../utils/vendorSubscription';

/**
 * Custom hook for managing vendor spotlight form data
 * Handles vendor selection and auto-population from vendor_profile
 */
export const useVendorSpotlightForm = ({ isOpen, isEdit, spotlight }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    rating: '',
    review_count: '',
    completed_events: '',
    location: '',
    image: '',
    profile_image: '',
    features: [''],
    badge: '',
    years_in_business: '',
    starting_price: '',
    vendor_id: '',
    sort_order: 0,
    is_active: true,
  });

  const [selectedVendorId, setSelectedVendorId] = useState('');

  // Fetch active vendors for selection
  const { data: vendorsResponse, isLoading: isLoadingVendors } = useAdminVendors(
    {
      per_page: 100,
      verified: true, // Get verified vendors
    },
    {
      enabled: isOpen && !isEdit, // Only fetch when modal is open and not editing
    }
  );

  // Fetch vendor details when selected
  const { data: vendorDetails } = useAdminVendor(selectedVendorId, {
    enabled: !isEdit, // Only fetch when not editing
  });

  // Populate form when vendor is selected
  useEffect(() => {
    if (vendorDetails?.data && !isEdit) {
      const vendor = vendorDetails.data;
      const vendorProfile = vendor.vendor_profile || {};
      const user = vendor.user || {};

      // Build location string
      const locationParts = [];
      if (vendorProfile.city) locationParts.push(vendorProfile.city);
      if (vendorProfile.state) locationParts.push(vendorProfile.state);
      const location = locationParts.join(', ') || '';

      // Get category from first service category or vendor category
      const firstService = vendorProfile.vendor_services?.[0];
      const firstCategory = vendorProfile.vendor_categories?.[0];
      const category = firstCategory?.service_category?.category_name || 
                       firstService?.service_category?.category_name ||
                       vendorProfile.business_type || 
                       '';

      // Get starting price from first service or package
      const startingPrice = firstService?.base_price || 
                           firstService?.vendor_service_packages?.[0]?.price || 
                           vendorProfile.vendor_service_packages?.[0]?.price ||
                           '';

      // Get features from specialties or create from services
      let features = [];
      if (vendorProfile.specialties && Array.isArray(vendorProfile.specialties)) {
        features = vendorProfile.specialties;
      } else if (vendorProfile.vendor_services && vendorProfile.vendor_services.length > 0) {
        features = vendorProfile.vendor_services.slice(0, 5).map(s => s.service_name || s.name);
      }
      if (features.length === 0) {
        features = [''];
      }

      setFormData(prev => ({
        ...prev,
        name: vendorProfile.business_name || vendor.name || '',
        category: category,
        description: vendorProfile.description || vendorProfile.bio || '',
        rating: vendorProfile.average_rating || '',
        review_count: vendorProfile.total_reviews || '',
        completed_events: vendorProfile.bookings?.length || 0,
        location: location,
        image: vendorProfile.image_url || user.profile_image_url || '',
        profile_image: user.profile_image_url || vendorProfile.image_url || '',
        features: features,
        badge: vendorProfile.is_featured ? 'Featured Vendor' : '',
        years_in_business: vendorProfile.years_experience || '',
        starting_price: startingPrice,
        vendor_id: vendorProfile.id || '', // Use vendor_profile.id (vendor_spotlights.vendor_id references vendor_profiles.id)
      }));
    }
  }, [vendorDetails, isEdit]);

  // Initialize form data when editing or modal opens/closes
  useEffect(() => {
    if (isEdit && spotlight) {
      setFormData({
        name: spotlight.name || '',
        category: spotlight.category || '',
        description: spotlight.description || '',
        rating: spotlight.rating || '',
        review_count: spotlight.review_count || '',
        completed_events: spotlight.completed_events || '',
        location: spotlight.location || '',
        image: spotlight.image || '',
        profile_image: spotlight.profile_image || '',
        features: spotlight.features && spotlight.features.length > 0 ? spotlight.features : [''],
        badge: spotlight.badge || '',
        years_in_business: spotlight.years_in_business || '',
        starting_price: spotlight.starting_price || '',
        vendor_id: spotlight.vendor_id || '',
        sort_order: spotlight.sort_order || 0,
        is_active: spotlight.is_active !== undefined ? spotlight.is_active : true,
      });
    } else if (!isEdit && isOpen) {
      // Reset form when creating
      setFormData({
        name: '',
        category: '',
        description: '',
        rating: '',
        review_count: '',
        completed_events: '',
        location: '',
        image: '',
        profile_image: '',
        features: [''],
        badge: '',
        years_in_business: '',
        starting_price: '',
        vendor_id: '',
        sort_order: 0,
        is_active: true,
      });
      setSelectedVendorId('');
    }
  }, [isEdit, spotlight, isOpen]);

  // Get vendor options for SearchSelect
  // Handle paginated response: vendorsResponse.data.data (Laravel pagination)
  // The response structure is: { success: true, data: { data: [...vendors] } }
  const vendors = vendorsResponse?.data?.data || vendorsResponse?.data || [];
  
  const vendorOptions = Array.isArray(vendors) ? vendors
    .filter(isVendorEligibleForSpotlight)
    .map((vendor) => {
      const businessName = vendor.vendor_profile?.business_name || vendor.name || 'Vendor';
      const city = vendor.vendor_profile?.city || '';
      const state = vendor.vendor_profile?.state || '';
      const location = city && state ? `${city}, ${state}` : city || state || '';
      const label = location ? `${businessName} - ${location}` : businessName;
      
      return {
        value: vendor.id,
        label: label,
      };
    }) : [];

  return {
    formData,
    setFormData,
    selectedVendorId,
    setSelectedVendorId,
    vendorOptions,
    isLoadingVendors,
    vendorsResponse, // For debugging
  };
};

